/**
 * /reset/request — unauthenticated forgot-password form.
 *
 * Mirrors the login page's chrome (centered card on black). The
 * success message is deliberately non-committal — "if an account
 * exists" — to match the server's enumeration-safe response.
 */
import { Link, createRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { rootRoute } from './root.js';
import { requestPasswordReset } from '../api/auth.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';

const FormSchema = z.object({
  orgSlug: z
    .string()
    .min(3)
    .max(63)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Slug is lowercase letters, digits, hyphens'),
  email: z.string().email('Enter a valid email'),
});
type FormValues = z.infer<typeof FormSchema>;

export const resetRequestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset/request',
  component: ResetRequestPage,
});

function ResetRequestPage(): JSX.Element {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { orgSlug: '', email: '' },
  });

  async function onSubmit(values: FormValues): Promise<void> {
    setServerError(null);
    try {
      await requestPasswordReset(values);
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setServerError('Too many requests — try again in a few minutes.');
      } else {
        setServerError('Could not submit the request. Try again.');
      }
    }
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-black text-white">
      <div className="w-full max-w-sm space-y-8 p-8">
        <header className="text-center space-y-2">
          <h1 className="font-serif text-3xl">LumoHR</h1>
          <p className="text-sm text-gray-400">Reset your password</p>
        </header>

        {submitted ? (
          <div className="space-y-6">
            <div
              role="status"
              className="rounded border border-green-900 bg-green-950/40 p-4 text-sm text-green-300"
            >
              If an account exists, we&apos;ve sent a reset link. The
              link expires in 30 minutes.
            </div>
            <p className="text-center text-sm text-gray-400">
              <Link to="/login" className="underline hover:text-white">
                Back to sign in
              </Link>
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-400">
              Enter your organization slug and email. If a matching
              account exists, we&apos;ll email you a reset link.
            </p>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
              <Field
                id="orgSlug"
                label="Organization slug"
                placeholder="my-company"
                autoComplete="organization"
                error={errors.orgSlug?.message}
                inputProps={register('orgSlug')}
              />
              <Field
                id="email"
                label="Email"
                type="email"
                placeholder="you@company.com"
                autoComplete="email"
                error={errors.email?.message}
                inputProps={register('email')}
              />

              {serverError ? <Alert variant="error">{serverError}</Alert> : null}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded bg-white text-black font-medium py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending…' : 'Send reset link'}
              </button>
            </form>
            <p className="text-center text-sm text-gray-400">
              Remembered it?{' '}
              <button
                type="button"
                onClick={() => void navigate({ to: '/login' })}
                className="underline hover:text-white"
              >
                Sign in
              </button>
            </p>
          </>
        )}
      </div>
    </main>
  );
}

interface FieldProps {
  id: string;
  label: string;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  error?: string;
  inputProps: ReturnType<ReturnType<typeof useForm<FormValues>>['register']>;
}

function Field({
  id,
  label,
  type = 'text',
  placeholder,
  autoComplete,
  error,
  inputProps,
}: FieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs uppercase tracking-wide text-gray-400">
        {label}
      </label>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-invalid={error ? 'true' : 'false'}
        className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white placeholder-gray-600 focus:border-white focus:outline-none"
        {...inputProps}
      />
      {error ? <p className="text-xs text-red-400">{error}</p> : null}
    </div>
  );
}
